import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Animated,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import { useLanguage } from '@/contexts/LanguageContext';
import onboardingService, { OnboardingStatus, OnboardingAction } from '@/services/onboardingService';

interface OnboardingCardProps {
  title: string;
  isCompleted: boolean;
  onPress: () => void;
}

const CheckmarkIcon = () => (
  <Svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <Path
      d="M7.91732 2.99463L3.9069 7.00505L2.08398 5.18213"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const SkeletonCard = ({ animatedValue }: { animatedValue: Animated.Value }) => {
  const opacity = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.4, 0.8],
  });

  return (
    <View style={styles.skeletonCard}>
      <Animated.View style={[styles.skeletonCheckbox, { opacity }]} />
      <View style={styles.skeletonTextContainer}>
        <Animated.View style={[styles.skeletonTextLine, { opacity }]} />
        <Animated.View style={[styles.skeletonTextLineShort, { opacity }]} />
      </View>
    </View>
  );
};

const OnboardingCard = ({ title, isCompleted, onPress }: OnboardingCardProps) => {
  return (
    <TouchableOpacity
      style={[
        styles.card,
        isCompleted ? styles.cardCompleted : styles.cardIncomplete,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View
        style={[
          styles.checkbox,
          isCompleted ? styles.checkboxCompleted : styles.checkboxIncomplete,
        ]}
      >
        {isCompleted && <CheckmarkIcon />}
      </View>
      <Text style={styles.cardTitle}>{title}</Text>
    </TouchableOpacity>
  );
};

interface OnboardingCarouselProps {
  onNavigateToSobriety?: () => void;
  onNavigateToMeetings?: () => void;
  onNavigateToTrail?: () => void;
  onNavigateToDiary?: () => void;
  onNavigateToProfile?: () => void;
  onSendFirstMessage?: () => void;
}

export function OnboardingCarousel({
  onNavigateToSobriety,
  onNavigateToMeetings,
  onNavigateToTrail,
  onNavigateToDiary,
  onNavigateToProfile,
  onSendFirstMessage,
}: OnboardingCarouselProps) {
  const { t } = useLanguage();
  const router = useRouter();
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const skeletonAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (loading) {
      const animation = Animated.loop(
        Animated.sequence([
          Animated.timing(skeletonAnim, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(skeletonAnim, {
            toValue: 0,
            duration: 800,
            useNativeDriver: true,
          }),
        ])
      );
      animation.start();
      return () => animation.stop();
    }
  }, [loading, skeletonAnim]);

  const fetchStatus = useCallback(async () => {
    try {
      const onboardingStatus = await onboardingService.getStatus();
      setStatus(onboardingStatus);
    } catch (error) {
      console.error('[OnboardingCarousel] Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleCardPress = (action: OnboardingAction) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);

    switch (action) {
      case 'sobriety_set':
        if (onNavigateToSobriety) {
          onNavigateToSobriety();
        } else {
          router.push('/sobriety-clock');
        }
        break;
      case 'first_message':
        if (onSendFirstMessage) {
          onSendFirstMessage();
        }
        break;
      case 'first_checkin':
        if (onNavigateToMeetings) {
          onNavigateToMeetings();
        } else {
          router.push('/encontros-online');
        }
        break;
      case 'first_trail_step':
        if (onNavigateToTrail) {
          onNavigateToTrail();
        } else {
          router.push('/trail');
        }
        break;
      case 'first_diary_note':
        if (onNavigateToDiary) {
          onNavigateToDiary();
        } else {
          router.push({ pathname: '/diary', params: { openAddNote: 'true' } });
        }
        break;
      case 'profile_photo_set':
        if (onNavigateToProfile) {
          onNavigateToProfile();
        } else {
          router.push('/perfil');
        }
        break;
    }
  };

  const allCompleted = status && 
    status.sobriety_set && 
    status.first_message && 
    status.first_checkin && 
    status.first_trail_step && 
    status.first_diary_note && 
    status.profile_photo_set;

  if (allCompleted) {
    return null;
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.skeletonTitleContainer}>
          <Animated.View style={[styles.skeletonTitle, { opacity: skeletonAnim.interpolate({ inputRange: [0, 1], outputRange: [0.4, 0.8] }) }]} />
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          style={styles.scrollView}
        >
          {[1, 2, 3, 4].map((i) => (
            <SkeletonCard key={i} animatedValue={skeletonAnim} />
          ))}
        </ScrollView>
      </View>
    );
  }

  const cards = [
    {
      action: 'sobriety_set' as OnboardingAction,
      title: t('onboarding.sobrietySet'),
      isCompleted: status?.sobriety_set || false,
    },
    {
      action: 'first_message' as OnboardingAction,
      title: t('onboarding.firstMessage'),
      isCompleted: status?.first_message || false,
    },
    {
      action: 'first_checkin' as OnboardingAction,
      title: t('onboarding.firstCheckin'),
      isCompleted: status?.first_checkin || false,
    },
    {
      action: 'first_trail_step' as OnboardingAction,
      title: t('onboarding.firstTrailStep'),
      isCompleted: status?.first_trail_step || false,
    },
    {
      action: 'first_diary_note' as OnboardingAction,
      title: t('onboarding.firstDiaryNote'),
      isCompleted: status?.first_diary_note || false,
    },
    {
      action: 'profile_photo_set' as OnboardingAction,
      title: t('onboarding.profilePhoto'),
      isCompleted: status?.profile_photo_set || false,
    },
  ];

  const sortedCards = [...cards].sort((a, b) => {
    if (a.action === 'first_message') return 1;
    if (b.action === 'first_message') return -1;
    if (a.isCompleted === b.isCompleted) return 0;
    return a.isCompleted ? 1 : -1;
  });

  return (
    <View style={styles.container}>
      <Text style={styles.sectionTitle}>{t('onboarding.title')}</Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        {sortedCards.map((card) => (
          <OnboardingCard
            key={card.action}
            title={card.title}
            isCompleted={card.isCompleted}
            onPress={() => handleCardPress(card.action)}
          />
        ))}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 12,
    paddingBottom: 12,
    paddingHorizontal: 12,
    gap: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
  },
  scrollView: {
    marginHorizontal: -12,
  },
  scrollContent: {
    paddingHorizontal: 12,
    gap: 8,
  },
  skeletonCard: {
    width: 130,
    height: 90,
    borderRadius: 12,
    padding: 12,
    backgroundColor: '#f0eee4',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  skeletonCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: '#dad8cf',
  },
  skeletonTextContainer: {
    gap: 4,
  },
  skeletonTextLine: {
    width: 90,
    height: 10,
    borderRadius: 4,
    backgroundColor: '#dad8cf',
  },
  skeletonTextLineShort: {
    width: 60,
    height: 10,
    borderRadius: 4,
    backgroundColor: '#dad8cf',
  },
  skeletonTitleContainer: {
    height: 14,
  },
  skeletonTitle: {
    width: 100,
    height: 14,
    borderRadius: 4,
    backgroundColor: '#dad8cf',
  },
  card: {
    width: 130,
    height: 90,
    borderRadius: 12,
    padding: 12,
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardIncomplete: {
    backgroundColor: '#f0eee4',
  },
  cardCompleted: {
    backgroundColor: '#e6ebf0',
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxIncomplete: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#7e7d75',
  },
  checkboxCompleted: {
    backgroundColor: '#015480',
  },
  cardTitle: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 16.8,
  },
});

export default OnboardingCarousel;
